import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, MapPin, Building2, Loader2, ClipboardCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

export default function IncaricoPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {ok, message}
  const [showRefuse, setShowRefuse] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    axios.get(`${API}/incarico/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Link non valido o non più attivo."))
      .finally(() => setLoading(false));
  }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      const body = kind === "rifiuta" ? { note } : undefined;
      const { data: res } = await axios.post(`${API}/incarico/${token}/${kind}`, body);
      setDone(res);
    } catch (e) {
      setDone({ ok: false, message: e.response?.data?.detail || "Operazione non riuscita. Riprova." });
    } finally { setBusy(false); }
  };

  const fmtDate = (s) => { if (!s) return ""; try { return new Date(s).toLocaleDateString("it-IT"); } catch { return s; } };

  return (
    <div className="min-h-screen bg-[#1A1818] flex flex-col items-center px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={EMBLEM} alt="Ginius" className="w-12 h-12 rounded-xl ring-2 ring-[#D4AF5A]/70" />
        <div className="leading-none">
          <div className="font-brand font-bold text-3xl text-brand" style={{ WebkitTextStroke: "0.5px #fff" }}>Ginius<span className="text-[#D4AF5A]">.</span></div>
          {data?.titolare?.ragione_sociale && <div className="text-xs text-[#E7C9A0]" data-testid="public-header-studio">{data.titolare.ragione_sociale}</div>}
        </div>
      </div>

      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6" data-testid="incarico-public-card">
        {loading && <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…</div>}

        {!loading && error && <div className="text-center py-8 text-red-700 font-semibold" data-testid="incarico-error">{error}</div>}

        {!loading && data && !done && (
          <>
            <div className="flex items-center gap-2 text-brand font-bold text-lg mb-1"><ClipboardCheck className="w-6 h-6" /> Incarico {data.numero}</div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3" data-testid="incarico-titolo">{data.titolo}</h1>
            <div className="space-y-2 text-slate-700 text-sm mb-5">
              {data.categoria && <div><span className="font-semibold">Tipo:</span> {data.categoria}</div>}
              <div className="flex items-start gap-1.5"><Building2 className="w-4 h-4 mt-0.5 text-slate-400" /> {data.condominio_nome}</div>
              {data.ubicazione && <div className="flex items-start gap-1.5"><MapPin className="w-4 h-4 mt-0.5 text-slate-400" /> {data.ubicazione}</div>}
              {data.descrizione && <div className="bg-slate-50 border rounded-lg p-3 whitespace-pre-wrap">{data.descrizione}</div>}
              {data.data_scadenza && <div><span className="font-semibold">Termine indicato:</span> {fmtDate(data.data_scadenza)}</div>}
              {data.referenti?.length > 0 && (
                <div><span className="font-semibold">Contatti per l'intervento:</span>
                  <ul className="list-disc ml-5 mt-1">
                    {data.referenti.map((r, i) => <li key={i}>{[r.nome, r.unita && `int. ${r.unita}`, r.telefono && `tel. ${r.telefono}`].filter(Boolean).join(" · ")}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {data.gia_gestito ? (
              <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 font-semibold" data-testid="incarico-gia-gestito">
                Questo incarico risulta già gestito (stato: {data.stato}). Non è richiesta alcuna azione.
              </div>
            ) : !showRefuse ? (
              <div className="space-y-3">
                <p className="text-center text-slate-600">Confermi di prendere in carico questo intervento?</p>
                <button data-testid="incarico-accetta-btn" disabled={busy} onClick={() => act("accetta")}
                  className="w-full h-14 rounded-xl bg-green-700 hover:bg-green-800 text-white font-bold text-lg flex items-center justify-center gap-2 disabled:opacity-60">
                  <CheckCircle2 className="w-6 h-6" /> {busy ? "Attendere…" : "Accetto l'incarico"}
                </button>
                <button data-testid="incarico-rifiuta-open" disabled={busy} onClick={() => setShowRefuse(true)}
                  className="w-full h-12 rounded-xl border-2 border-red-300 text-red-700 font-semibold flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" /> Rifiuto
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-600 text-sm">Vuoi indicare un motivo? (facoltativo)</p>
                <textarea data-testid="incarico-rifiuta-note" value={note} onChange={(e) => setNote(e.target.value)}
                  className="w-full border-2 border-slate-300 rounded-lg p-3 h-24" placeholder="Motivo del rifiuto (opzionale)" />
                <button data-testid="incarico-rifiuta-conferma" disabled={busy} onClick={() => act("rifiuta")}
                  className="w-full h-12 rounded-xl bg-red-700 hover:bg-red-800 text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <XCircle className="w-5 h-5" /> {busy ? "Attendere…" : "Conferma rifiuto"}
                </button>
                <button onClick={() => setShowRefuse(false)} className="w-full h-10 text-slate-500">Annulla</button>
              </div>
            )}
          </>
        )}

        {done && (
          <div className="text-center py-8" data-testid="incarico-done">
            {done.ok ? <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-3" /> : <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-3" />}
            <p className="text-lg font-semibold text-slate-800">{done.message}</p>
            <p className="text-sm text-slate-500 mt-2">Puoi chiudere questa pagina.</p>
          </div>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-6" data-testid="public-footer-studio">{data?.titolare?.ragione_sociale || "Ginius"} · Gestione manutenzioni</p>
    </div>
  );
}
